import React, { useState, useEffect } from "react";
import { Form, Input, Button, Select, Upload, Result, notification, Spin, message, Modal } from "antd";
import { iam_user, iam_affiliated, iam_producer, iam_partner } from "./iam";
import { InboxOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { withAuth } from "@salespark/api-client-v2";
import { createAPIClient } from "utils/helper";
import uniqid from "uniqid";

const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

const SupportRequestForm = (props) => {
  const [form] = Form.useForm();

  const { auth } = props;
  const [state, setState] = useState({ iam: undefined });
  const [supportRequestUniqueId] = useState(uniqid());
  const [fileLoading, setFileLoading] = useState(false);
  const [uploadFileList, setUploadFileList] = useState({ fileList: [] });
  const [authData, setAuthData] = useState(undefined);
  const [postStatus, setPostStatus] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const apiHandler = withAuth(createAPIClient());

  useEffect(() => {
    if (auth) {
      setAuthData(auth);
      setState((prev) => ({
        ...prev,
        email: auth.email,
        name: auth.name,
        uniqueId: supportRequestUniqueId,
      }));
      form.setFieldsValue({
        email: auth.email,
        name: auth.name,
        uniqueId: supportRequestUniqueId,
      });
    }
  }, [auth, form, supportRequestUniqueId]);

  const styleFormItem = {
    style: {
      width: "100%",
      maxWidth: "450px",
    },
  };

  const uploadAction = (data) => {
    return `${process.env.REACT_APP_API_URL}/upload/public/file/support/documents/?content_type=${data.type}&uniqueId=${supportRequestUniqueId}`;
  };

  const uploadProps = {
    name: "file",
    accept: ".png, .jpeg, .jpg, .webp, .pdf",
    multiple: true,
    showUploadList: true,
    maxCount: 10,
    action: uploadAction,
    beforeUpload: beforeUpload,
    onChange(info) {
      try {
        const { status } = info.file;

        if (status === "uploading") {
          setFileLoading(true);
        }
        if (status === "done") {
          setUploadFileList({ fileList: info.fileList });
          setFileLoading(false);

          message.success("Ficheiro carregado com sucesso!");
        } else if (status === "error") {
          notification.error({
            duration: 3,
            message: "Erro",
            description: "Erro ao carregar o ficheiro...",
          });
          setFileLoading(false);
        }
      } catch (error) {
        notification.error({
          duration: 3,
          message: "Erro",
          description: "Erro ao carregar o ficheiro...",
        });
        setFileLoading(false);
      }
    },
  };

  function beforeUpload(file) {
    const isValidFile = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp" || file.type === "application/pdf";
    if (!isValidFile) {
      notification.error({
        duration: 2,
        message: "Erro",
        description: "Tens de selecionar um ficheiro válido!",
      });
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      notification.error({
        duration: 2,
        message: "Erro",
        description: "o ficheiro não pode ter mais do que 5MB!",
      });
    }

    return isValidFile && isLt5M;
  }

  const populateReasons = (iam) => {
    if (iam === undefined) return <></>;
    let jsonData = [];

    switch (iam) {
      case "iam-user":
        jsonData = iam_user;
        break;
      case "iam-affiliated":
        jsonData = iam_affiliated;
        break;
      case "iam-producer":
        jsonData = iam_producer;
        break;
      case "iam-partner":
        jsonData = iam_partner;
        break;
      default:
        jsonData = ["Outro assunto"];
        break;
    }

    return jsonData.map((el) => (
      <Option key={el} value={el}>
        {el}
      </Option>
    ));
  };

  const handleSubjectChange = (e) => {
    setState((prev) => ({ ...prev, iam: e, reason: undefined }));
    form.setFieldsValue({ iam: e, reason: undefined });
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    values.fileList = parseFileList(values.fileList);
    let res = await AddNewSupportRequest(values);

    setIsLoading(false);

    if (res.status) {
      setPostStatus(true);
    }
  };

  const parseFileList = (files) => {
    if (!files) return [];
    if (!Array.isArray(files.fileList)) return [];
    if (files.fileList.length === 0) return [];

    return files.fileList
      .filter((el) => el.response?.url)
      .map((el) => ({ uid: el.uid, name: el.name, status: "done", url: el.response.url }));
  };

  const AddNewSupportRequest = async (data) => {
    const res = await apiHandler.post("/actions/support-request", data);

    if (!res.status) {
      Modal.error({
        title: "Oops!",
        content: (
          <>
            Ocorreu um erro ao submeter o teu pedido.
            <br />
            Tenta novamente mais tarde.
            <br />
            <br />
            Caso o erro persista, contacta-nos através do e-mail: <strong>suporte@salespark.io</strong>.
          </>
        ),
      });
      return { status: false, data: res.data };
    }

    return { status: true, data: res.data };
  };

  const onFinishFailed = (values) => {
    notification.error({
      duration: 3,
      message: "Erro",
      description: "Preenche corretamente todos os campos obrigatórios para submeter o pedido de suporte.",
    });
  };

  return (
    <>
      <div style={{ display: postStatus === true ? "block" : "none" }}>
        <Result
          status="success"
          title="O teu pedido foi enviado com sucesso!"
          subTitle={
            <>
              Podes também contactar o suporte através do e-mail{" "}
              <a href="mailto:suporte@salespark.io">suporte@salespark.io</a>
            </>
          }
          extra={[
            <a href="https://ajuda.salespark.io" target="_blank" rel="noopener noreferrer">
              <Button type="primary" key="console">
                Ir para a Central de Ajuda
              </Button>
            </a>,
          ]}
        />
      </div>
      <div style={{ display: postStatus === undefined ? "block" : "none" }}>
        <Spin spinning={isLoading}>
          <Form layout="vertical" initialValues={state} form={form} style={{ textAlign: "left" }} onFinish={onFinish} onFinishFailed={onFinishFailed}>
            <Form.Item name="uniqueId" style={{ display: "none" }}>
              <Input type={"hidden"} />
            </Form.Item>

            <Form.Item
              name="iam"
              label="Assunto"
              {...styleFormItem}
              rules={[
                {
                  required: true,
                  message: "Campo Obrigatório",
                },
              ]}
              hasFeedback
            >
              <Select onChange={(e) => handleSubjectChange(e)}>
                <Option value="iam-user">Compra de produto na SalesPark</Option>
                <Option value="iam-producer">Sou um Produtor</Option>
                <Option value="iam-partner">Sou um Parceiro</Option>
                <Option value="iam-affiliated">Sou um Afiliado</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="reason"
              label="Motivo do contacto"
              {...styleFormItem}
              rules={[
                {
                  required: true,
                  message: "Campo Obrigatório",
                },
              ]}
              hasFeedback
            >
              <Select disabled={state?.iam === undefined}>{populateReasons(state?.iam)}</Select>
            </Form.Item>

            <Form.Item
              name="name"
              label="Nome"
              {...styleFormItem}
              rules={[
                {
                  required: true,
                  message: "Campo Obrigatório",
                },
              ]}
              hasFeedback
            >
              <Input disabled={authData?.name !== undefined} />
            </Form.Item>

            <Form.Item
              name="email"
              label="Endereço de e-mail"
              {...styleFormItem}
              rules={[
                {
                  required: true,
                  message: "Campo Obrigatório",
                },
              ]}
              hasFeedback
            >
              <Input disabled={authData?.email !== undefined} type="email" />
            </Form.Item>

            <Form.Item
              label="Descrição"
              name="description"
              rules={[
                {
                  required: true,
                  message: "Campo Obrigatório",
                },
                {
                  min: 40,
                  message: "A descrição tem de ter mais de 40 caracteres.",
                },
                {
                  max: 800,
                  message: "A descrição não pode conter mais de 800 caracteres.",
                },
              ]}
              hasFeedback
            >
              <TextArea rows={6} />
            </Form.Item>

            <Form.Item label="Anexos (opcional)" name="fileList">
              <Dragger {...uploadProps} disabled={uploadFileList.fileList?.length >= 10}>
                {fileLoading ? (
                  <Spin tip="A carregar ficheiro..."></Spin>
                ) : (
                  <div>
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Clica aqui ou arrasta para aqui a imagem</p>
                    <p className="ant-upload-hint">Suporte apenas a ficheiros de imagem comuns e PDF's (.png, .jpeg, .jpg, .webp, .pdf)</p>
                  </div>
                )}
              </Dragger>
            </Form.Item>

            <Form.Item>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="large" type="primary" htmlType="submit">
                  Submeter Pedido de Suporte <ArrowRightOutlined />
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </>
  );
};

export default SupportRequestForm;
